import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { DataTableAdvanced, type DataTableColumn } from '@/components/ui/data-table-advanced'
import { apiService } from '@/services/api'
import { Plus, MoreHorizontal, Edit, Trash2, Tag, Trash } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { format } from 'date-fns'
import { useDrawer } from '@/components/ui/drawer'
import { useDrawerMessaging } from '@/hooks/useDrawerMessaging'
import { toast } from 'sonner'
import { BlogTagForm } from '@/components/blog/BlogTagForm'

interface BlogTag {
  id: string
  name: string
  slug: string
  created_at: string
}

export const BlogTags: React.FC = () => {
  const { openDrawer, closeDrawer } = useDrawer()
  const { onMessage } = useDrawerMessaging('blog-tags-page')
  const [tags, setTags] = useState<BlogTag[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedTags, setSelectedTags] = useState<BlogTag[]>([])
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false)

  useEffect(() => {
    fetchTags()
  }, [])

  useEffect(() => {
    return onMessage((message) => {
      if (message.action === 'refresh') {
        fetchTags()
      }
    })
  }, [onMessage])

  const fetchTags = async () => {
    try {
      setLoading(true)
      const data = await apiService.blog.tags.list()
      setTags(data)
    } catch (err) {
      console.error('Failed to fetch blog tags:', err)
      toast.error('Failed to load article tags')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateTag = () => {
    const drawerId = 'create-blog-tag'
    openDrawer({
      id: drawerId,
      title: 'Create Article Tag',
      content: <BlogTagForm
        onSuccess={() => {
          closeDrawer(drawerId)
          fetchTags()
          toast.success('Tag created successfully')
        }}
      />,
      footer: null,
      size: 'md',
    })
  }

  const handleEditTag = (tag: BlogTag) => {
    const drawerId = `edit-blog-tag-${tag.id}`
    openDrawer({
      id: drawerId,
      title: `Edit: ${tag.name}`,
      content: <BlogTagForm
        tag={tag}
        onSuccess={() => {
          closeDrawer(drawerId)
          fetchTags()
          toast.success('Tag updated successfully')
        }}
      />,
      footer: null,
      size: 'md',
    })
  }

  const handleDeleteTag = async (tag: BlogTag) => {
    if (!confirm(`Are you sure you want to delete "${tag.name}"?`)) {
      return
    }

    try {
      await apiService.blog.tags.delete(tag.id)
      toast.success('Article tag deleted successfully')
      fetchTags()
    } catch (err) {
      console.error('Failed to delete blog tag:', err)
      toast.error('Failed to delete article tag')
    }
  }

  const handleBulkDelete = async () => {
    if (selectedTags.length === 0) return

    setLoading(true)
    try {
      // Delete tags one by one (could be optimized with bulk API endpoint)
      await Promise.all(
        selectedTags.map(tag => apiService.blog.tags.delete(tag.id))
      )

      toast.success(`${selectedTags.length} tag(s) deleted successfully`)
      setSelectedTags([])
      setShowBulkDeleteDialog(false)
      fetchTags()
    } catch (err) {
      console.error('Failed to delete tags:', err)
      toast.error('Failed to delete some tags')
    } finally {
      setLoading(false)
    }
  }

  const columns: DataTableColumn<BlogTag>[] = [
    {
      id: 'name',
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          <Tag className="h-4 w-4 text-muted-foreground" />
          <div>
            <div className="font-medium">{row.original.name}</div>
            <div className="text-sm text-muted-foreground">/{row.original.slug}</div>
          </div>
        </div>
      ),
    },
    {
      id: 'created_at',
      accessorKey: 'created_at',
      header: 'Created',
      cell: ({ row }) => (
        <div className="text-sm text-muted-foreground">
          {format(new Date(row.original.created_at), 'MMM d, yyyy')}
        </div>
      ),
    },
    {
      id: 'actions',
      accessorKey: 'id',
      header: '',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuItem onClick={() => handleEditTag(row.original)}>
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => handleDeleteTag(row.original)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Article Tags</h1>
          <p className="text-muted-foreground">
            Tag your articles for better organization and discovery
          </p>
        </div>
        <div className="flex gap-2">
          {selectedTags.length > 0 && (
            <Button
              onClick={() => setShowBulkDeleteDialog(true)}
              variant="destructive"
              className="gap-2"
            >
              <Trash className="h-4 w-4" />
              Delete Selected ({selectedTags.length})
            </Button>
          )}
          <Button onClick={handleCreateTag} className="gap-2">
            <Plus className="h-4 w-4" />
            New Tag
          </Button>
        </div>
      </div>

      {/* Data Table */}
      <DataTableAdvanced
        data={tags}
        columns={columns}
        loading={loading}
        searchable={true}
        searchKeys={['name']}
        searchPlaceholder="Search tags..."
        emptyMessage="No article tags found"
        showSelection={true}
        onRowSelect={setSelectedTags}
      />

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedTags.length} tag(s)?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The following tags will be permanently deleted:
              <div className="mt-2 max-h-32 overflow-y-auto">
                {selectedTags.map(tag => (
                  <div key={tag.id} className="text-sm">
                    • {tag.name}
                  </div>
                ))}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Tags
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}